import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { V2, addV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { Activatable } from 'game/behaviors/Activatable';
import { FILTER_OUTLINE_ID } from 'game/defs/FilterOutline';
import { easeOutQuart } from 'game/lib/easing';
import { inGameUIClasses } from 'game/styles/inGameUI';
import clamp from 'lodash/clamp';
import { createElement } from 'react';
import { CotSprite, TentFlapInnerSprite, TentFlapOuterSprite, TentIntSprite, WASDToMove } from './TentIntSprite';

const wasdOffset: V2 = [-80, 360];
let shouldShowWasd = true;

class TentIntBehavior extends Facet<{ wasdSprite: StaticSpriteFacet }> {

  fadeOutStart = Infinity;
  fadeDuration = 1000;
  entityStart = 0;

  start(engine: Engine) {
    this.entityStart = engine.time.nowUnscaled;
    shouldShowWasd = false;
  }

  update(delta: number, engine: Engine) {
    const { wasdSprite } = this.props;
    if (engine.time.nowUnscaled < this.fadeOutStart) {
      const startDelay = 1000;
      const startDelta = engine.time.nowUnscaled - this.entityStart;
      const progress = easeOutQuart(clamp((startDelta - startDelay) / this.fadeDuration, 0, 1));
      wasdSprite.props.opacity = progress;
      wasdSprite.props.offset = addV2([0, progress * -20], wasdOffset);

      if (engine.input.keyDown.KeyD) {
        this.fadeOutStart = Math.max(
          engine.time.nowUnscaled,
          this.entityStart + startDelay + this.fadeDuration,
        );
      }
      return;
    }

    const deltaInteraction = engine.time.nowUnscaled - this.fadeOutStart;
    const progress = deltaInteraction / this.fadeDuration;
    if (progress >= 1) {
      this.entity.removeFacet(this.props.wasdSprite);
      this.entity.removeFacet(this);
      return;
    }
    this.props.wasdSprite.props.opacity = 1 - progress;
  }
}

export class TentIntEntity extends Entity {
  createFacets() {
    const wasdSprite = create(StaticSpriteFacet, { frame: WASDToMove, offset: wasdOffset, opacity: 0 });
    const wasdFacets = (shouldShowWasd) ? [
      wasdSprite,
      create(TentIntBehavior, { wasdSprite }),
    ] : [];

    return [
      create(StaticSpriteFacet, { frame: TentIntSprite }),
      ...wasdFacets,

      // walls
      create(ColliderFacet, { type: ColliderType.Box, size: [520, 100], offset: [0, 690] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [100, 360], offset: [510, 335] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [100, 360], offset: [-90, 335] }),
      create(ColliderFacet, {
        type: ColliderType.Polygon,
        offset: [0, 0],
        points: [[0, 330], [522, 330], [522, 425], [362, 425]],
      }),

      // cot
      create(ColliderFacet, { type: ColliderType.Box, size: [163, 30], offset: [0, 545] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [180, 152], offset: [0, 322] }),
    ];
  }
}

export class CotEntity extends Entity {
  createFacets() {
    return [
      create(StaticSpriteFacet, { frame: CotSprite, offset: [-80, -316] }),
    ];
  }
}

interface TentFlapProps {
  onExit: () => void,
}

class TentFlapBehavior extends Facet<TentFlapProps> {
  update(delta: number, engine: Engine) {
    if (engine.input.keyDown.KeyE) {
      this.props.onExit();
    }
  }
}

export class TentFlapEntity extends Entity<TentFlapProps> {
  createFacets(props: TentFlapProps) {
    const outerSprite = create(StaticSpriteFacet, {
      frame: TentFlapOuterSprite,
      offset: [-87, -186],
      opacity: 0.5,
    });
    const outerGroup = create(SpriteGroupFacet, { children: [outerSprite] });
    const behavior = create(TentFlapBehavior, props);
    let text: StaticSpriteFacet | null = null;

    return [
      outerGroup,
      create(StaticSpriteFacet, { frame: TentFlapInnerSprite, offset: [-87, -186] }),
      create(ColliderFacet, {
        type: ColliderType.Box,
        size: [220, 40],
        offset: [-110, -30],
        trigger: true,
      }),
      create(Activatable, {
        activate: (active, engine) => {
          outerGroup.props.filter = active ? `url(#${FILTER_OUTLINE_ID})` : undefined;
          outerSprite.props.opacity = active ? 1 : 0.5;

          if (active) {
            text = create(StaticSpriteFacet, {
              frame: createElement('text', {
                className: inGameUIClasses.activatedText,
                y: -210,
              }, '[e] Exit'),
            });
            this.addFacet(text, engine);
            this.addFacet(behavior, engine);
            behavior.props.onExit = props.onExit;
          } else if (text) {
            this.removeFacet(text);
            this.removeFacet(behavior);
          }
        },
      }),
    ];
  }
}
